/**
 * Hōjin — Logo system (Phase A)
 * Lotus géométrique 7 pétales · or discret · kanji 法人
 */
import * as React from "react";

type SymbolSize = "md" | "lg" | "xl";
type KanjiSize = "sm" | "md";

function cx(...c: Array<string | false | undefined>) {
  return c.filter(Boolean).join(" ");
}

export function NanaeLogoSymbol({
  size = "md",
  className,
  title = "Hōjin",
}: {
  size?: SymbolSize;
  className?: string;
  title?: string;
}) {
  const stroke = "var(--qv3-pearl, #F7F2EA)";
  const gradId = React.useId().replace(/:/g, "");
  const linearId = `nanae-pearl-${gradId}`;
  const glowId = `nanae-glow-${gradId}`;
  return (
    <span className={cx("nanae-logo-symbol", `size-${size}`, className)}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        overflow="visible"
        fill="none"
        stroke={`url(#${linearId})`}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <title>{title}</title>
        <defs>
          {/* Dégradé nacré : du blanc lumineux en haut au pearl plus sourd en bas */}
          <linearGradient id={linearId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="45%" stopColor="#FBF7F0" stopOpacity="0.98" />
            <stop offset="100%" stopColor="#D9CFBE" stopOpacity="0.92" />
          </linearGradient>
          {/* Halo radial blanc qui respire derrière le logo */}
          <radialGradient id={`${linearId}-halo`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="0.85" />
            <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </radialGradient>
          {/* Halo doux pour décoller le logo des fonds clairs */}
          <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Halo respirant — derrière tout le reste */}
        <circle
          cx="100"
          cy="100"
          r="96"
          fill={`url(#${linearId}-halo)`}
          stroke="none"
          className="hojin-halo"
        />
        <g filter={`url(#${glowId})`}>


        {/* Sphère extérieure */}
        <g transform="translate(100, 100)">
          {/* Cercle extérieur principal */}
          <circle cx="0" cy="0" r="82" strokeWidth="6" />

          {/* Anneaux intérieurs animés (armillaire 3D simulée)
              chaque anneau = cercle parfait + groupe de bascule (scale
              oscillant qui le réduit en ligne) + groupe de rotation. */}
          <g className="hojin-armilla">
            {/* Anneau 1 — bascule sur axe horizontal */}
            <g className="hojin-spin hojin-spin-1">
              <g className="hojin-tilt hojin-tilt-1">
                <ellipse cx="0" cy="0" rx="62" ry="62" strokeWidth="3" className="hojin-ring" />
              </g>
            </g>
            {/* Anneau 2 — bascule sur axe vertical */}
            <g className="hojin-spin hojin-spin-2">
              <g className="hojin-tilt hojin-tilt-2">
                <ellipse cx="0" cy="0" rx="50" ry="50" strokeWidth="3" className="hojin-ring" />
              </g>
            </g>
            {/* Anneau 3 — bascule diagonale */}
            <g className="hojin-spin hojin-spin-3">
              <g className="hojin-tilt hojin-tilt-3">
                <ellipse cx="0" cy="0" rx="38" ry="38" strokeWidth="2.4" className="hojin-ring" />
              </g>
            </g>
          </g>

          {/* Axe central */}
          <circle cx="0" cy="0" r="3" fill={`url(#${linearId})`} stroke="none" />
        </g>
        </g>
      </svg>


    </span>
  );
}


export function NanaeWordmark({
  size = "md",
  className,
  as: As = "span",
}: {
  size?: SymbolSize;
  className?: string;
  as?: "span" | "h1" | "div";
}) {
  return (
    <As className={cx("nanae-wordmark", `size-${size}`, className)}>
      <span role="img" aria-label="Hōjin">H<span className="hojin-o" aria-hidden="true">o</span>jin</span>
    </As>
  );
}

export function NanaeKanji({
  size = "sm",
  className,
}: {
  size?: KanjiSize;
  className?: string;
}) {
  return (
    <span
      className={cx("nanae-kanji", `size-${size}`, className)}
      role="img"
      aria-label="Hōjin (法人)"
      title="法人"
    >
      法人
    </span>
  );
}

export function NanaeLogoFull({
  layout = "horizontal",
  symbolSize,
  wordmarkSize,
  withKanji = true,
  className,
}: {
  layout?: "horizontal" | "vertical";
  symbolSize?: SymbolSize;
  wordmarkSize?: SymbolSize;
  withKanji?: boolean;
  className?: string;
}) {
  const ss: SymbolSize = symbolSize ?? (layout === "vertical" ? "xl" : "md");
  const ws: SymbolSize = wordmarkSize ?? (layout === "vertical" ? "xl" : "md");
  return (
    <div className={cx("nanae-logo-full", `layout-${layout}`, className)}>
      <NanaeLogoSymbol size={ss} />
      <NanaeWordmark size={ws} as={layout === "vertical" ? "h1" : "span"} />
      {withKanji && <NanaeKanji size={layout === "vertical" ? "md" : "sm"} />}
    </div>
  );
}
